import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import capitalize from "lodash/capitalize";

import { userPlanSelector } from "src/store/system/selector";
import {
  getPoshmarkIntegrations,
  getMercariIntegrations,
  getWhatnotIntegrations,
  getVintedIntegrations,
  getDepopIntegrations,
} from "src/store/plaidIntegration/selector";
import type { IntegrationType } from "src/store/integrationSync/types";
import {
  isResumeSyncStatus,
  type ResumeSyncStatus,
} from "src/store/integrationSync/types";
import { updateSyncStatus } from "src/store/integrationSync/actions";
import {
  clearBeforeUnloadHandler,
  type IntegrationPlatformName,
} from "src/store/integrationSync/actions/utils";
import { syncWithCheck } from "src/store/integrationSync/actions";
import {
  vintedDismissResume,
  vintedSyncInventory,
  vintedSyncSales,
} from "src/store/integrationSync/actions/vinted";
import {
  getVintedAbandonedResumeStatus,
  getVintedActiveResumeLeaseExpiresAt,
} from "src/store/integrationSync/actions/vinted/vintedResumeOffer";
import {
  depopDismissResume,
  depopSyncInventory,
  depopSyncSales,
} from "src/store/integrationSync/actions/depop";
import {
  getDepopAbandonedResumeStatus,
  getDepopActiveResumeLeaseExpiresAt,
} from "src/store/integrationSync/actions/depop/depopResumeOffer";
import { integrationSyncEntriesSelector } from "src/store/integrationSync/selector";
import { integrationSyncEntrySelector } from "src/store/integrationSync/selector";
import AddPoshmarkExtensionDialog from "src/components/AddPoshmarkExtensionDialog";

import PoshmarkLogInDialog from "./LogInDialog";
import StatusesContainer from "./StatusesContainer";
import Status, { StatusProps } from "./Status";

const CheckInterval = 1 * 60 * 1000; // 1 min

function Syncer({
  integrationId,
  platform,
}: {
  integrationId: string;
  platform: IntegrationPlatformName;
}) {
  const dispatch = useDispatch();
  const dispatchRef = useRef(dispatch);

  dispatchRef.current = dispatch;

  useEffect(() => {
    let timeout;
    async function effect() {
      timeout = undefined;

      await dispatchRef.current(syncWithCheck(integrationId));

      timeout = setTimeout(effect, CheckInterval);
    }

    effect();

    return () => {
      if (timeout) clearTimeout(timeout);
      clearBeforeUnloadHandler(platform);
    };
  }, [integrationId, platform]);

  return null;
}

/**
 * Offer Resume when a direct-import lease expired with a checkpoint.
 * Shared by Vinted and Depop (platform-specific offer helpers only).
 *
 * Lease expiry is wall-clock only — a crashed tab leaves a stale lease with no
 * further Firestore writes. While a checkpoint sits under a still-fresh lease
 * we schedule a one-shot recheck 1s after the known expiry (`*IsRunning +
 * ttl`). Heartbeats update `*IsRunning` and remount this effect (clearing +
 * rescheduling the timer).
 */
const ResumeOfferExpiryGraceMs = 1000;

function DirectImportResumeWatcher<T extends { id: string }>({
  integrationKey,
  integrations,
  getAbandonedResumeStatus,
  getActiveResumeLeaseExpiresAt,
}: {
  integrationKey: Extract<IntegrationType, "vinted" | "depop">;
  integrations: T[];
  getAbandonedResumeStatus: (integration: T) => ResumeSyncStatus | undefined;
  getActiveResumeLeaseExpiresAt: (integration: T) => number | undefined;
}) {
  const dispatch = useDispatch();
  const status = useSelector((s) =>
    integrationSyncEntrySelector(s, integrationKey)
  );
  const getAbandonedResumeStatusRef = useRef(getAbandonedResumeStatus);
  getAbandonedResumeStatusRef.current = getAbandonedResumeStatus;
  const getActiveResumeLeaseExpiresAtRef = useRef(
    getActiveResumeLeaseExpiresAt
  );
  getActiveResumeLeaseExpiresAtRef.current = getActiveResumeLeaseExpiresAt;

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined;

    function check() {
      if (
        status?.type === "syncing" ||
        status?.type === "locked" ||
        status?.type === "error"
      ) {
        return;
      }

      for (const integration of integrations) {
        const offer = getAbandonedResumeStatusRef.current(integration);
        if (!offer) continue;
        if (
          isResumeSyncStatus(status) &&
          status.integrationId === integration.id &&
          status.job === offer.job
        ) {
          break;
        }
        dispatch(
          updateSyncStatus({ integration: integrationKey, status: offer })
        );
        break;
      }

      // Clear a stale resume banner when no offer remains (after evaluating).
      const anyOffer = integrations.some((integration) =>
        getAbandonedResumeStatusRef.current(integration)
      );
      if (!anyOffer && status?.type === "resume") {
        dispatch(
          updateSyncStatus({ integration: integrationKey, status: undefined })
        );
      }

      let earliestExpiresAt: number | undefined;
      for (const integration of integrations) {
        const expiresAt = getActiveResumeLeaseExpiresAtRef.current(integration);
        if (expiresAt == null) continue;
        if (earliestExpiresAt == null || expiresAt < earliestExpiresAt) {
          earliestExpiresAt = expiresAt;
        }
      }
      if (earliestExpiresAt == null) return;

      const delayMs = Math.max(
        0,
        earliestExpiresAt + ResumeOfferExpiryGraceMs - Date.now()
      );
      timeout = setTimeout(check, delayMs);
    }

    check();

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [integrations, status, dispatch, integrationKey]);

  return null;
}

function IntegrationSyncerDialog() {
  const plan = useSelector(userPlanSelector);
  const poshmarkIntegrations = useSelector(getPoshmarkIntegrations);
  const mercariIntegrations = useSelector(getMercariIntegrations);
  const whatnotIntegrations = useSelector(getWhatnotIntegrations);
  const vintedIntegrations = useSelector(getVintedIntegrations);
  const depopIntegrations = useSelector(getDepopIntegrations);
  const entries = useSelector(integrationSyncEntriesSelector);
  const dispatch = useDispatch();
  const [resumeLoading, setResumeLoading] = useState(false);
  const [dismissLoading, setDismissLoading] = useState(false);
  let manualDialog:
    | {
        integration: IntegrationType;
        type: "install-extension" | "update-extension" | "user-not-logged-in";
      }
    | undefined;

  const snackbars = (entries || []).map((state) => {
    const snackbarProps: StatusProps & { integration: string } = {
      integration: state.integration,
      open: false,
    };

    if (state?.type === "syncing") {
      snackbarProps.open = true;
      snackbarProps.message = `Your ${capitalize(
        state.integration
      )} account is syncing. Please don’t close your browser until this process completes.${
        state.description ? ` ${state.description}` : ""
      }`;
    } else if (state?.type === "locked") {
      snackbarProps.open = true;
      snackbarProps.message = state.message;
    } else if (state?.type === "resume") {
      snackbarProps.open = true;
      snackbarProps.message = state.message;
      snackbarProps.severity = "info";
      snackbarProps.actionLabel = "Resume";
      snackbarProps.actionLoading = resumeLoading;
      snackbarProps.onAction = async () => {
        setResumeLoading(true);
        try {
          if (state.integration === "depop") {
            if (state.job === "sales-import" && state.start && state.end) {
              await dispatch(
                depopSyncSales(state.integrationId, {
                  start: state.start,
                  end: state.end,
                  resume: true,
                })
              );
            } else if (state.job === "inventory-import") {
              await dispatch(
                depopSyncInventory(state.integrationId, { resume: true })
              );
            }
          } else if (state.integration === "vinted") {
            if (state.job === "sales-import" && state.start && state.end) {
              await dispatch(
                vintedSyncSales(state.integrationId, {
                  start: state.start,
                  end: state.end,
                  resume: true,
                })
              );
            } else if (state.job === "inventory-import") {
              await dispatch(
                vintedSyncInventory(state.integrationId, { resume: true })
              );
            }
          }
        } finally {
          setResumeLoading(false);
        }
      };
      snackbarProps.closeDisabled = dismissLoading;
      snackbarProps.onClose = async () => {
        if (dismissLoading) return;
        setDismissLoading(true);
        try {
          if (state.integration === "depop") {
            await dispatch(depopDismissResume(state.integrationId, state.job));
          } else if (state.integration === "vinted") {
            await dispatch(vintedDismissResume(state.integrationId, state.job));
          }
        } finally {
          setDismissLoading(false);
        }
      };
    } else if (state?.type === "error") {
      snackbarProps.open = true;
      snackbarProps.message = state.message || "Error";
      snackbarProps.severity = "warning";
      snackbarProps.onClose = () =>
        dispatch(
          updateSyncStatus({
            integration: state.integration,
            status: undefined,
          })
        );
      if (
        state.dispatcher === "manual" &&
        (state.code === "install-extension" ||
          state.code === "update-extension" ||
          state.code === "user-not-logged-in")
      ) {
        snackbarProps.open = false;
        manualDialog = {
          type: state.code,
          integration: state.integration,
        };
      }
    }

    return snackbarProps;
  });

  return (
    <>
      <AddPoshmarkExtensionDialog
        open={
          manualDialog?.type === "install-extension" ||
          manualDialog?.type === "update-extension"
        }
        platform={manualDialog?.integration}
        update={manualDialog?.type === "update-extension"}
        onClose={() => {
          if (!manualDialog) return;
          dispatch(
            updateSyncStatus({
              integration: manualDialog.integration,
              status: undefined,
            })
          );
        }}
      />
      <PoshmarkLogInDialog
        open={manualDialog?.type === "user-not-logged-in"}
        platform={manualDialog?.integration}
        onClose={() => {
          if (!manualDialog) return;
          dispatch(
            updateSyncStatus({
              integration: manualDialog.integration,
              status: undefined,
            })
          );
        }}
      />
      <DirectImportResumeWatcher
        integrationKey="vinted"
        integrations={vintedIntegrations}
        getAbandonedResumeStatus={getVintedAbandonedResumeStatus}
        getActiveResumeLeaseExpiresAt={getVintedActiveResumeLeaseExpiresAt}
      />
      <DirectImportResumeWatcher
        integrationKey="depop"
        integrations={depopIntegrations}
        getAbandonedResumeStatus={getDepopAbandonedResumeStatus}
        getActiveResumeLeaseExpiresAt={getDepopActiveResumeLeaseExpiresAt}
      />
      {plan === "ultimate"
        ? [
            ...poshmarkIntegrations.map((integration) => ({
              integration,
              platform: "Poshmark" as const,
            })),
            ...mercariIntegrations.map((integration) => ({
              integration,
              platform: "Mercari" as const,
            })),
            ...whatnotIntegrations.map((integration) => ({
              integration,
              platform: "Whatnot" as const,
            })),
            ...vintedIntegrations.map((integration) => ({
              integration,
              platform: "Vinted" as const,
            })),
            ...depopIntegrations.map((integration) => ({
              integration,
              platform: "Depop" as const,
            })),
          ].map(({ integration, platform }) => {
            if (!integration.sync) return null;

            return (
              <Syncer
                key={integration.id}
                integrationId={integration.id}
                platform={platform}
              />
            );
          })
        : null}
      <StatusesContainer>
        {snackbars.map(({ integration, ...snackbarProps }) => (
          <Status key={integration} {...snackbarProps} />
        ))}
      </StatusesContainer>
    </>
  );
}

export default IntegrationSyncerDialog;
